import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Form,
  FormField,
  TextInput,
  Heading,
  Select,
  CheckBox,
  Layer,
  ResponsiveContext,
  Text,
  Anchor,
} from "grommet";
import { useNavigate } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { countryList } from "../../data/countryList";
import { AuthContext } from "../../contexts/AuthContext";
import axios from "axios";
import { Info } from "grommet-icons";
import API_URL from "../../config/config";

const CreateProfile = () => {
  const navigate = useNavigate();
  const size = React.useContext(ResponsiveContext);
  const { user } = useContext(AuthContext);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showDBErrorPopup, setShowDBErrorPopup] = useState(false);
  const { login } = useContext(AuthContext);
  const [decodedToken, setDecodedToken] = useState(null);
  const [inviteStatus, setInviteStatus] = useState(false);

  // Local state to manage form data
  const [formData, setFormData] = useState({
    country: "",
    organization: "",
    role: "",
    isVerified: true,
    isManager: 0, // 0 for individual, 1 for team or both
    agreeToCommunicate: false,
    managerOption: "", // New field to track the selected option
  });
  const [hpeEducationAccepted, setHpeEducationAccepted] = useState(false); // New state for the HPE Education checkbox
  const checkInvite = async () => {
    try {
      if (!user) {
        setShowErrorPopup(true);
        return;
      }

      setDecodedToken(user);
      const user_id =
        user.role === "manager" ? user.user_table_id : user.user_id;
      const response = await axios.get(
        `${API_URL}/manager/check_invite_status/${user_id}`,
      );
      
      if (response.data.inviteStatus === true) {
        setInviteStatus(true);
      }
    } catch (error) {
      console.error("Error checking invite status:", error);
      setShowDBErrorPopup(true);
    }
  };
  const setToken = async () => { };

  useEffect(() => {
    if (!user) return;
    setFormData((prevData) => ({
      ...prevData,
      fullName: user.name || prevData.fullName,
      organization: user.organization || prevData.organization,
    }));
    setToken();
    checkInvite();

    // Redirect away if the user already has a completed profile
    const isManagerUser = user.role === "manager" || Boolean(user.user_table_id);
    const user_id = isManagerUser ? user.user_table_id : user.user_id;
    if (user_id) {
      axios.get(`${API_URL}/user/getProfileStatus/${user_id}`)
        .then((res) => {
          if (res.data?.is_profile_completed === 1) {
            if (isManagerUser) {
              navigate(`/${user.user_id}/manager/dashboard`, { replace: true });
            } else {
              navigate(`/add-proficiency/${user_id}`, { replace: true });
            }
          }
        })
        .catch(() => {}); // new user — no profile yet, stay on page
    }
  }, [user]);

  // Function to handle input changes
  const handleInputChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Function to handle checkbox changes for isVerified and ads
  const handleCheckboxChange = (name, checked) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  // Function to handle manager option change
  const handleIsManagerChange = ({ option }) => {
    
    setFormData((prevData) => ({
      ...prevData,
      managerOption: option,
      isManager:
        option === "I want to analyze my team's skill gaps" ||
          option === "I want to analyze both"
          ? 1
          : 0,
    }));
  };

  const fetchJwt = async () => {
    try {
      const newUserResponse = await axios.get(`${API_URL}/user/newuser`);
      return newUserResponse;
    } catch (error) {
      console.error("Error fetching JWT:", error);
      setShowDBErrorPopup(true);
      return null;
    }
  };

  // Function to handle form submission
  const handleSubmit = async () => {
    const { user_id } = decodedToken;
    const id = user_id;

    const formDataToSend = {
      id,
      organization: formData.organization,
      country: formData.country,
      role: formData.role,
      isManager: formData.isManager,
      agree_to_communicate: formData.agreeToCommunicate ? 1 : 0,
    };

    try {
      const res = await axios.post(`${API_URL}/user/addUser`, {
        formDataToSend,
      });
      const { user_id: newUserId, isManager } = res.data;
      

      if (isManager === 0) {
        navigate(`/add-proficiency/${newUserId}`, { replace: true });
      } else {
        const newUserResponse = await fetchJwt();
        if (newUserResponse?.data?.user) {
          login(newUserResponse.data.user);
          navigate(`/${newUserId}/manager/dashboard`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      setShowDBErrorPopup(true);
    }
  };

  const label = `Company name (Please provide the name of your current employer)`;

  return (
    <Box fill="vertical" justify="between">
      <Box flex="grow">
        <HeaderNavigation restrictedMode />
        <Box align="center" justify="center" pad="medium">
          <Heading
            level="2"
            size="large"
            weight="bold"
            textAlign="center"
            margin={{ bottom: "medium" }}
          >
            HPE Digital Skill Advisor Profile
          </Heading>
          <Box width={size === "small" ? "100%" : "medium"}>
            <Form
              value={formData}
              // Removed onChange to prevent overriding isManager
              onReset={() => {
                setFormData({
                  country: "",
                  organization: "",
                  role: "",
                  isVerified: false,
                  isManager: 0,
                  agreeToCommunicate: false,
                  managerOption: "",
                });
                setHpeEducationAccepted(false);
              }}
              onSubmit={handleSubmit}
            >
              <FormField label="Country" name="country" required>
                <Select
                  name="country"
                  options={countryList || []}
                  value={formData.country}
                  onChange={({ option }) =>
                    handleInputChange("country", option)
                  }
                  placeholder="Select Country"
                  size="medium"
                />
              </FormField>

              <FormField label={label} name="organization" required>
                <TextInput
                  name="organization"
                  value={formData.organization}
                  onChange={(event) =>
                    handleInputChange("organization", event.target.value)
                  }
                  size="medium"
                />
              </FormField>
              <FormField label="Please enter your current role" name="role">
                <TextInput
                  name="role"
                  value={formData.role}
                  onChange={(event) =>
                    handleInputChange("role", event.target.value)
                  }
                  size="medium"
                />
              </FormField>

              {/* Updated dropdown field for defining the role */}
              {!inviteStatus && (
                <FormField
                  label="What best defines you?"
                  name="isManager"
                  info={
                    <Box
                      background={{ color: "light-2" }}
                      pad="small"
                      round="xsmall"
                      direction="row"
                      gap="small"
                    >
                      <Info color="brand" />
                      <Text size="small" color="dark-6">
                        If you are a manager, select{" "}
                        <b>
                          &quot;I want to analyze my team&apos;s skill
                          gaps&quot;
                        </b>{" "}
                        or <b>&quot;I want to analyze both&quot;</b> to gain
                        insights into your team&apos;s performance. <br />
                        Select{" "}
                        <b>&quot;I want to analyze my skill gaps&quot;</b> if
                        you wish to focus solely on your personal development.
                      </Text>
                    </Box>
                  }
                  required
                >
                  <Select
                    name="isManager"
                    options={[
                      "I want to analyze my skill gaps",
                      "I want to analyze my team's skill gaps",
                      "I want to analyze both",
                    ]}
                    value={formData.managerOption}
                    onChange={handleIsManagerChange}
                    placeholder="Select an option"
                    size="medium"
                  />
                </FormField>
              )}

              <Box margin={{ top: "medium" }}>
                {/* <FormField
                  htmlFor="terms-and-privacy-simple"
                  name="termsAndPrivacy"
                >
                  <CheckBox
                    id="terms-and-privacy-simple"
                    name="termsAndPrivacy"
                    checked={formData.isVerified}
                    onChange={(event) =>
                      handleCheckboxChange("isVerified", event.target.checked)
                    }
                    label={
                      <Text>
                        I accept the HPE{" "}
                        <Anchor
                          label="Terms of Use"
                          href="https://www.hpe.com/us/en/about/end-user-agreement-terms.html"
                          target="_blank"
                          rel="noreferrer noopener"
                        />{" "}
                        and{" "}
                        <Anchor
                          label="Privacy Policy"
                          href="https://www.hpe.com/in/en/legal/privacy.html"
                          target="_blank"
                          rel="noreferrer noopener"
                        />
                        *
                      </Text>
                    }
                  />
                </FormField> */}
                <FormField htmlFor="hpe-education" name="hpeEducation">
                  <CheckBox
                    id="hpe-education"
                    name="hpeEducation"
                    checked={hpeEducationAccepted}
                    onChange={(event) =>
                      setHpeEducationAccepted(event.target.checked)
                    }
                    label={
                      <Text>
                        I accept the HPE Education{" "}
                        <Anchor
                          label="Terms of Use"
                          href="https://www.hpe.com/us/en/collaterals/collateral.a50009008enw.html"
                          target="_blank"
                          rel="noreferrer noopener"
                        />{" "}
                        *
                      </Text>
                    }
                  />
                </FormField>
                <FormField htmlFor="agreeToCommunicate" name="agreeToCommunicate">
                  <CheckBox
                    id="agreeToCommunicate"
                    name="agreeToCommunicate"
                    onChange={(event) =>
                      handleCheckboxChange("agreeToCommunicate", event.target.checked)
                    }
                    checked={formData.agreeToCommunicate}
                    label={
                      <Text>
                        I accept to be contacted by HPE for communications
                        related to HPE Digital Skill Advisor.
                      </Text>
                    }
                  />
                </FormField>
              </Box>

              <Box
                direction={size === "small" ? "column" : "row"}
                gap="medium"
                margin={{ top: "medium" }}
              >
                <Button
                  type="submit"
                  label="Create profile"
                  primary
                  size="medium"
                  disabled={
                    !formData.isVerified ||
                    !hpeEducationAccepted ||
                    !formData.country ||
                    !formData.organization ||
                    // formData.managerOption === ""
                    (!inviteStatus && formData.managerOption === "")
                  }
                />
              </Box>
            </Form>
          </Box>
        </Box>
      </Box>
      <StickyFooter />
      {/* Error Popups */}
      {showErrorPopup && (
        <Layer
          onEsc={() => setShowErrorPopup(false)}
          onClickOutside={() => setShowErrorPopup(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Text weight="bold" color="status-critical">
              Error
            </Text>
            <Text>
              User already exists. Please try with a different account or
              contact support.
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button label="Close" onClick={() => setShowErrorPopup(false)} />
            </Box>
          </Box>
        </Layer>
      )}
      {showDBErrorPopup && (
        <Layer
          onEsc={() => setShowDBErrorPopup(false)}
          onClickOutside={() => setShowDBErrorPopup(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Text weight="bold" color="status-critical">
              Error
            </Text>
            <Text>
              There has been an error creating your profile. Please try again.
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="Close"
                onClick={() => setShowDBErrorPopup(false)}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default CreateProfile;
